import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import type { SurveyTrainingRecommendation, SurveyTrainingRecommendationPersonnelRow } from '../surveyTypes';

const ID = 'training-impact';

type Result = '' | 'passed' | 'failed';

/** A sample assessment document — synthetic, suffixed so each run is told apart. */
function sampleAssessment(): string {
  const tag = uid().slice(0, 6).toUpperCase();

  return [
    `<p><strong>Sample impact assessment ${tag}</strong></p>`,
    '<p>Applies what the training covered on live assignments.</p>',
    '<ul><li><p>Observed: clearer written reports within the month.</p></li><li><p>Next: one more cycle with a mentor.</p></li></ul>',
  ].join('');
}

/**
 * A training recommendation's IMPACT ASSESSMENT (product call, 13 Oct 2026) — ONE assessment of
 * the training, recorded on EVERY employee the recommendation was sent to. The form asks two
 * things and nothing else (the employee picker and the level reached were removed the same day):
 *
 * - **Result** — Passed or Failed, side by side. Two radios, never a checkbox: it opens
 *   UNANSWERED on a first assessment, because "failed" for a training nobody assessed would assert it.
 * - **Assessment** — a rich-text document, sanitised on write.
 *
 * Revisable: where it has been assessed before, the form opens on what was recorded. Their report
 * is not waited for. Writing takes `surveys.assess-impact`, in ONE request — all or none. An
 * employee above the reader's access level is left out, and the form says how many it covers.
 * The editor is uncontrolled after mount, so a loaded or sample document is shown by REMOUNTING
 * it under a bumped `key`.
 */
export function SurveyTrainingImpactModal({
  open,
  recommendation,
  onClose,
  onSaved,
}: {
  open: boolean;
  recommendation: SurveyTrainingRecommendation;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  // The employees it is recorded on. Null while loading.
  const [rows, setRows] = useState<SurveyTrainingRecommendationPersonnelRow[] | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [result, setResult] = useState<Result>('');
  // Null until what was recorded before (if anything) has been read — the editor mounts after.
  const [assessment, setAssessment] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // The plan's employees, one page of a hundred — then those it was SENT to.
    surveyTrainingRecommendationsApi
      .personnel(recommendation.id, { pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const sent = page.items.filter((r) => r.isSent && !r.isRestricted);

        // Assessed before: open on what was recorded. The document rides the single read only.
        const recorded = sent.find((r) => r.impactAssessmentAt != null);
        const track = recorded
          ? await surveyTrainingRecommendationsApi.track(recommendation.id, recorded.respondentId, controller.signal).catch(() => null)
          : null;

        if (controller.signal.aborted) return;

        setRows(sent);
        setResult(recorded?.impactPassed == null ? '' : recorded.impactPassed ? 'passed' : 'failed');
        setAssessment(track?.impactAssessment ?? '');
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load the employees.', 0));
      });

    return () => controller.abort();
  }, [recommendation.id]);

  const employees = rows ?? [];

  // An empty editor is `<p></p>`: read the text, not the string.
  const written = (assessment ?? '').replace(/<[^>]*>/g, '').trim() !== '';
  const canSave = employees.length > 0 && result !== '' && written && confirmed && !saving;

  async function save() {
    if (!canSave || assessment === null) return;

    setSaving(true);
    setError(null);

    try {
      await surveyTrainingRecommendationsApi.saveImpactAssessments(
        recommendation.id,
        employees.map((r) => ({
          respondentId: r.respondentId,
          passed: result === 'passed',
          // The form no longer asks for a level; one recorded earlier is kept as it was.
          achievedLevel: r.impactAchievedLevel ?? null,
          assessment,
        })),
      );

      toast.success(`Saved the impact assessment of "${recommendation.title}".`);
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The impact assessment could not be saved.', 0));
    } finally {
      setSaving(false);
    }
  }

  const ready = rows !== null && assessment !== null && !loadError;
  const any = employees.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Impact assessment — ${recommendation.title}`}
      // A step wider than the other forms here (product call, 13 Oct 2026) — the document is the form.
      size="2xl"
      id={`${ID}-modal`}
      autogenerate={
        ready && any
          ? {
              onGenerate: () => {
                setResult(Math.random() < 0.8 ? 'passed' : 'failed');
                setAssessment(sampleAssessment());
                setEditorKey((k) => k + 1);
              },
              disabled: saving,
            }
          : undefined
      }
      confirm={ready && any ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        ready && any ? (
          <>
            <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </>
        ) : (
          <Button id={`${ID}-close`} variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {loadError ? (
          <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
        ) : !ready ? (
          <Skeleton className="h-40 w-full" />
        ) : !any ? (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            This training recommendation has not reached any employee yet. Add personnel to its plan, and the
            impact can be assessed.
          </p>
        ) : (
          <>
            {/* The consequence, before the tick: with no picker, the form has to say who it covers. */}
            <p className="text-xs text-slate-600">
              {`This assessment is recorded on ${employees.length === 1 ? 'the 1 employee' : `all ${employees.length} employees`} this training recommendation was sent to.`}
            </p>

            {/* Passed and Failed SIDE BY SIDE (product call, 13 Oct 2026) — the group's own two columns. */}
            <RadioGroup<Result>
              id={`${ID}-result`}
              label="Result"
              required
              value={result}
              onChange={setResult}
              options={[
                { value: 'passed', label: 'Passed' },
                { value: 'failed', label: 'Failed' },
              ]}
              columns={2}
              disabled={saving}
            />

            <LazyRichTextField
              key={editorKey}
              id={`${ID}-assessment`}
              label="Assessment"
              description="What the training changed."
              required
              value={assessment ?? ''}
              onChange={setAssessment}
              maxLength={8000}
              disabled={saving}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
